import { CATEGORY_ORDER, getActiveSources } from './types';
import { SOIL_SOURCES } from './categories/soil';
import { LAND_CAPABILITY_SOURCES } from './categories/land-capability';
import { FLOOD_SOURCES } from './categories/flood';
import { NITRATE_SOURCES, NITRATE_WATER_SOURCES } from './categories/nitrate';
import { DESIGNATION_SOURCES } from './categories/designations';

// Exactly one map layer may be visible at a time — see applyToggle in
// lib/useMapLayers.js, which is where that rule is enforced.
//
// This replaced a pairwise incompatibility matrix. By the time it was removed the
// matrix listed all six pairs of the four categories, so it was already a complete
// graph: every layer excluded every other one, and the machinery only added a way
// to get it wrong. A new category had to remember to declare four exclusions or it
// would silently stack under the others.
export const CATALOGUE = [
  {
    id: 'soil',
    label: 'Soil type',
    description: 'Soil type and association across your fields.',
    sources: SOIL_SOURCES,
    stackLevel: 1,
  },
  {
    id: 'land-capability',
    // The two nations use genuinely different schemes, so the toggle name follows
    // the region on screen. The shortened names below were confirmed
    // 11/09/2026: each nation keeps its own scheme's name.
    label: 'Land capability',
    regionLabels: {
      scotland: 'Land capability',
      england: 'Land classification',
      wales: 'Land classification',
    },
    description: 'Agricultural land classification by grade.',
    sources: LAND_CAPABILITY_SOURCES,
    stackLevel: 1,
  },
  {
    id: 'flood',
    label: 'Flood risk',
    description: 'River, sea and surface water flood zones.',
    sources: FLOOD_SOURCES,
    stackLevel: 2,
  },
  {
    // Merged layer (BBB-456): protected conservation sites (SSSI/SAC/SPA) plus the
    // nitrate zones that are small enough to sit alongside them, as ONE toggle +
    // ONE legend card. Region determines what's present — England: 3 designations
    // (3 items); Scotland: 3 designations + NVZ (4); Wales: designated sites only
    // (3). Designation sources come first so the legend reads
    // conservation-then-regulatory. The Zone Details land panel intentionally keeps
    // these as separate rows — this merge is map/legend-only.
    //
    // England's NVZ and safeguard zones were in here and are not any more: their
    // fills cover too much of the country to share a toggle with anything. They
    // are the 'nitrate-water' layer below.
    id: 'protected-sites',
    // 'Protected sites', decided 11/09/2026. This layer carries conservation
    // designations, so "Regulatory areas" overstated it once England's nitrate
    // zones left. Its legend card said "Designated sites" at the same time, so
    // the same layer wore two names on one screen.
    label: 'Protected sites',
    description:
      'Protected conservation sites (SSSI, SAC, SPA) plus nitrate zones, where applicable.',
    sources: [...DESIGNATION_SOURCES, ...NITRATE_SOURCES],
    stackLevel: 2,
  },
  {
    // England only, by construction rather than by a region check: every source
    // here is region 'england', and MapLayerPanel hides a category with no
    // renderable sources for the active region. Adding a Scottish or Welsh source
    // to this list would make the toggle appear there too.
    id: 'nitrate-water',
    label: 'Nitrate and water zones',
    description:
      'Nitrate Vulnerable Zones and drinking water safeguard zones, in the official colours, kept faint so you can still see your fields underneath.',
    sources: NITRATE_WATER_SOURCES,
    stackLevel: 2,
  },
];

export const DEFAULT_LAYER_OPACITY = {
  soil: 0.7,
  'land-capability': 0.7,
  flood: 0.65,
  'protected-sites': 0.5,
  // Both sources in this category override this (0.30 and 0.35), so it is only a
  // floor for anything added later. Deliberately not the 1 that was here for the
  // full-strength WMS route Davin rejected on 18/08: a new source inheriting that
  // would reintroduce the exact defect.
  'nitrate-water': 0.3,
};

const SOURCE_CATEGORY = (() => {
  const map = {};
  for (const category of CATALOGUE) {
    for (const source of category.sources) {
      map[source.id] = category.id;
    }
  }
  return map;
})();

export function resolveOpacity(source) {
  if (source.defaultOpacity !== undefined) return source.defaultOpacity;
  const categoryId = SOURCE_CATEGORY[source.id];
  return categoryId ? DEFAULT_LAYER_OPACITY[categoryId] : 0.6;
}

export { CATEGORY_ORDER, getActiveSources };
export { isRenderable } from './types';
